import type { POI } from "./poi";




/** Array of faltten {SourceTuple}'s */
export type SourceIndex = string[];
export type SourceTuple = [source: string, id: string];
/** Array of faltten {ResultTuple}'s */
export type ResultIndex = number[];
export type ResultTuple = [score: number, index: number];

type Search = (term: string, index: SourceIndex, treshold: number) => ResultIndex;
type Comparator = (str1: string, str2: string, gram_size?: number) => number;


const __search = (c: Comparator) => (term: string, index: SourceIndex, treshold = .05): ResultIndex => {
  let count = 0;
  const length = index.length;
  const result: ResultIndex = [];
  for (let idx = 0; idx < length; idx+=2) {
    const source = index[idx];
    const score = c(term, source);
    // console.log(`${score} >= ${treshold} =`, score >= treshold);
    
    if (score >= treshold) {
      // Sort by score
      for (let j = 0; j < count + 2; j+=2) {
        // console.log(` ${j}: ${score} >= ${result[j]} =`, score >= result[j], count);
        if (!result[j] || score >= result[j]) {
          result.splice(j, 0, score, idx, ...result.splice(j, Number.POSITIVE_INFINITY));
          count++
          break;
        }
      }
    }
  }

  return result;
}

export const search1 = __search(compareSimilarity1);
export function compareSimilarity1 (str1: string, str2: string, gram_size: number = 2) {
  // console.time();

  function getNGrams(s: string, len: number) {
    s = ' '.repeat(len - 1) + s.toLowerCase() + ' '.repeat(len - 1);
    let v = new Array(s.length - len + 1);
    for (let i = 0; i < v.length; i++) {
      v[i] = s.slice(i, i + len);
    }
    return v;
  }

  if (!str1?.length || !str2?.length) { return 0.0; }

  let s1 = str1.length < str2.length ? str1 : str2;
  let s2 = str1.length < str2.length ? str2 : str1;

  let pairs1 = getNGrams(s1, gram_size);
  let pairs2 = getNGrams(s2, gram_size);
  let set = new Set<string>(pairs1);

  let total = pairs2.length;
  let hits = 0;
  for (let item of pairs2) {
    if (set.delete(item)) {
      hits++;
    }
  }
  const result = hits / total;
  // console.timeEnd();
  // console.log(' score', result, str2);
  return result;
}

export const search2 = __search(compareSimilarity2);
export function compareSimilarity2 (str1: string, str2: string, gram_size: number = 2) {
  // console.time();

  function getNGrams (source: string, gram_size: number) {

    const r = ' '.repeat(gram_size - 1);
    source = r + source.toLowerCase() + r;
    const v_length = source.length - gram_size + 1;
    let v: string[] = new Array(v_length);
    for (let i = 0; i < v_length; i++) {
      v[i] = source.slice(i, i + gram_size);
    }
    return v;
  }

  if (!str1?.length || !str2?.length) { return 0.0; }

  let s1 = str1.length < str2.length ? str1 : str2;
  let s2 = str1.length < str2.length ? str2 : str1;

  let pairs1 = getNGrams(s1, gram_size);
  let pairs2 = getNGrams(s2, gram_size);
  let set = new Set<string>(pairs1);

  let total = pairs2.length;
  let hits = 0;
  for (let item of pairs2) {
    if (set.delete(item)) {
      hits++;
    }
  }
  const result = hits / total;
  // console.timeEnd();
  // console.log(' score', result, str2);
  return result;
}


const memo = new Map<string,string[]>();
export const search3 = __search(compareSimilarity3);
export function compareSimilarity3 (str1: string, str2: string, gram_size: number = 3) {
  // console.time();

  function getNGrams (source: string, length: number) {
    const m = memo.get(source);
    if (m) return m;

    const r = ' '.repeat(length - 1);
    source = r + source.toLowerCase() + r;
    const v_length = source.length - length + 1;
    let v: string[] = new Array(v_length);
    for (let i = 0; i < v_length; i++) {
      v[i] = source.slice(i, i + length);
    }
    memo.set(source, v);
    return v;
  }

  if (!str1?.length || !str2?.length) { return 0.0; }

  let s1 = str1.length < str2.length ? str1 : str2;
  let s2 = str1.length < str2.length ? str2 : str1;

  let pairs1 = getNGrams(s1, gram_size);
  let pairs2 = getNGrams(s2, gram_size);
  // console.log(pairs1, pairs2);
  
  let set = new Set<string>(pairs1);

  let total = pairs2.length;
  let hits = 0;
  for (let item of pairs2) {
    if (set.delete(item)) {
      hits++;
    }
  }
  const result = hits / total;
  // console.timeEnd();
  // console.log(' score', result, str2);
  return result;
}


function memoize<K,T>(fn: (key: K, ...args: any) => T) {
  const memo = new Map<K,T>();
  return function (key: K, ...args: any[]) {
    const m = memo.get(key);
    if (m) return m;
    const v = fn.call(undefined, key, ...args);
    memo.set(key, v);
    return v;
  }
}

export const search4 = __search(compareSimilarity4);
export function compareSimilarity4 (str1: string, str2: string, gram_size: number = 2) {
  // console.time();

  const getNGrams = memoize((source: string, length: number) => {
    const r = ' '.repeat(length - 1);
    source = r + source.toLowerCase() + r;
    const v_length = source.length - length + 1;
    let v: string[] = new Array(v_length);
    for (let i = 0; i < v_length; i++) {
      v[i] = source.slice(i, i + length);
    }
    return v;
  })

  if (!str1?.length || !str2?.length) { return 0.0; }

  let s1 = str1.length < str2.length ? str1 : str2;
  let s2 = str1.length < str2.length ? str2 : str1;

  let pairs1 = getNGrams(s1, gram_size);
  let pairs2 = getNGrams(s2, gram_size);
  let set = new Set<string>(pairs1);

  let total = pairs2.length;
  let hits = 0;
  for (let item of pairs2) {
    if (set.delete(item)) {
      hits++;
    }
  }
  const result = hits / total;
  // console.timeEnd();
  // console.log(' score', result, str2);
  return result;
}



// Select best version
export const search = search3;



type SectorData = [order: number, index: number, size: number];
const ORDER = 0, INDEX = 1, SIZE = 2;

export class SearchIndex {

  readonly sn_stores = "stores";

  private _index: SourceIndex;
  readonly sectors: Map<string,SectorData>;

  constructor () {
    this._index = [];
    this.sectors = new Map();
  }

  get index () {
    return this._index;
  }

  // updateStoreIndexes (stores: readonly ManageAPI.Store[]) {
  //   const indexes: SourceIndex = stores.reduce((acc: SourceIndex, store) => {
  //     const { spaceCode, name, fieldOfBusinessId } = store;
  //     acc.push(name, spaceCode);
  //     return acc;
  //   }, []);
  //   this.updateSector(this.sn_stores, indexes);
  // }

  // updatePOIIndexes (ps: readonly POI[]) {
  //   const indexes: SourceIndex = ps.reduce((acc: SourceIndex, p) => {
  //     const { spaceCode, name, fieldOfBusinessId } = p;
  //     acc.push(name, spaceCode);
  //     return acc;
  //   }, []);
  //   this.updateSector(this.sn_stores, indexes);
  // }

  updateSector (sector_name: string, indexes: SourceIndex) {
    const new_size = indexes.length;
    const sector = this.sectors.get(sector_name);
    if (sector) {
      const [order, index, size] = sector;
      const delta = new_size - size;
      this._index.splice(index, size, ...indexes);

      // Update sectors
      sector[SIZE] = new_size; 
      this.sectors.forEach((data, name) => {
        if (data[ORDER] > order) {
          data[INDEX] += delta;
        }
      });
    }
    else {
      const index = this._index.length;
      this._index.push(...indexes);
      this.sectors.set(sector_name, [this.sectors.size, index, new_size])
    }
  }

}
